import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Send, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SendInvoiceEmailDialog = ({ open, onOpenChange, invoice, customers, onSuccess }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [form, setForm] = useState({
    to_email: '',
    cc_email: '',
    subject: '',
    body: '',
    include_timesheets: false,
  });

  useEffect(() => {
    if (!open || !invoice) return;
    // Load templates
    axios.get(`${API}/api/email-templates`).then(r => {
      setTemplates(r.data);
      const def = r.data.find(t => t.is_default);
      if (def) applyTemplate(def, invoice);
    }).catch(() => {});

    // Pre-fill recipient from customer
    const customer = customers?.find(c => c.id === invoice.customer_id);
    const toEmail = customer?.invoice_email || customer?.email || '';
    const invNum = invoice.invoice_number || '';

    setForm({
      to_email: toEmail,
      cc_email: '',
      subject: `Invoice ${invNum}`,
      body: `<p>Dear Customer,</p><p>Please find attached invoice <strong>${invNum}</strong>.</p><p>Kind regards,<br>Expertfix</p>`,
      include_timesheets: false,
    });
    setSelectedTemplate('');
  }, [open, invoice, customers]);

  const applyTemplate = (template, inv) => {
    const invNum = inv?.invoice_number || invoice?.invoice_number || '';
    let subject = (template.subject || '').replace(/\{invoice_number\}/g, invNum);
    let body = (template.body || '').replace(/\{invoice_number\}/g, invNum);
    setForm(prev => ({ ...prev, subject, body }));
    setSelectedTemplate(template.id);
  };

  const handleTemplateChange = (templateId) => {
    if (templateId === 'none') {
      setSelectedTemplate('');
      return;
    }
    const t = templates.find(x => x.id === templateId);
    if (t) applyTemplate(t, invoice);
  };

  const handleSaveAsTemplate = async () => {
    const name = window.prompt('Template name:');
    if (!name) return;
    setSavingTemplate(true);
    try {
      const r = await axios.post(`${API}/api/email-templates`, {
        name,
        subject: form.subject.replace(invoice.invoice_number || '', '{invoice_number}'),
        body: form.body.replace(invoice.invoice_number || '', '{invoice_number}'),
        is_default: false,
      });
      setTemplates(prev => [...prev, r.data]);
      toast.success(t('invoiceExtra.templateSaved'));
    } catch { toast.error(t('invoiceExtra.failedToSaveTemplate')); }
    finally { setSavingTemplate(false); }
  };

  const handleSend = async () => {
    if (!form.to_email) { toast.error(t('invoiceExtra.recipientEmailIsRequired')); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/invoices/${invoice.id}/send-email`, form);
      toast.success(`Invoice sent to ${form.to_email}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>{t('invoiceExtra.sendInvoiceViaEmail')}</DialogTitle>
          <DialogDescription>Invoice {invoice.invoice_number}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {templates.length > 0 && (
            <div>
              <Label>{t('invoiceExtra.emailTemplate')}</Label>
              <Select value={selectedTemplate || 'none'} onValueChange={handleTemplateChange}>
                <SelectTrigger data-testid="email-template-select">
                  <SelectValue placeholder={t('invoiceExtra.selectATemplate')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('invoiceExtra.noTemplate')}</SelectItem>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>{t('invoiceExtra.to')}</Label>
            <Input value={form.to_email} onChange={e => setForm(p => ({...p, to_email: e.target.value}))}
              placeholder="recipient@example.com" data-testid="email-to" />
          </div>
          <div>
            <Label>{t('invoiceExtra.ccOptional')}</Label>
            <Input value={form.cc_email} onChange={e => setForm(p => ({...p, cc_email: e.target.value}))}
              placeholder="cc@example.com" data-testid="email-cc" />
          </div>
          <div>
            <Label>{t('invoiceExtra.subject')}</Label>
            <Input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))}
              data-testid="email-subject" />
          </div>
          <div>
            <Label>{t('invoiceExtra.body')}</Label>
            <Textarea value={form.body} onChange={e => setForm(p => ({...p, body: e.target.value}))}
              rows={6} data-testid="email-body" />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id="email_include_ts" checked={form.include_timesheets}
              onCheckedChange={v => setForm(p => ({...p, include_timesheets: v}))}
              data-testid="email-include-timesheets" />
            <Label htmlFor="email_include_ts">{t('invoiceExtra.includeTimesheetsInPdf')}</Label>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button variant="ghost" size="sm" onClick={handleSaveAsTemplate} disabled={savingTemplate} data-testid="save-email-template-btn">
            <Save className="h-4 w-4 mr-1" /> {t('invoiceExtra.saveAsTemplate')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{t('invoiceExtra.cancel')}</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="send-email-btn">
              <Send className="h-4 w-4 mr-2" />
              {sending ? t('invoiceExtra.sending') : t('invoiceExtra.sendEmail')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendInvoiceEmailDialog;
